import { action, computed, makeObservable, observable } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { GroupAction, SelectedGroup } from 'store/pages/groups-dashboard/types';
import { archiveGroup, removeGroup } from 'api/operations/groupService';

interface ConstructorParams {
  setPageLoading: (status: boolean) => void;
  updatePageData: () => Promise<void>;
}

export class GroupInteractionDelegate {
  private readonly _modals = {
    [GroupAction.REMOVE]: new ModalDelegate(),
    [GroupAction.ARCHIVE]: new ModalDelegate(),
  };

  private _selectedGroup: SelectedGroup = { id: '', name: '' };

  private _selectGroup(id: string, name: string): void {
    this._selectedGroup = { id, name };
  }

  private readonly _setPageLoading: (status: boolean) => void;
  private readonly _updatePageData: () => Promise<void>;

  public get selectedGroup(): SelectedGroup {
    return this._selectedGroup;
  }

  public get removeModal(): ModalDelegate {
    return this._modals[GroupAction.REMOVE];
  }

  public get archiveModal(): ModalDelegate {
    return this._modals[GroupAction.ARCHIVE];
  }

  public commitGroupAction(groupAction: GroupAction, groupId: string, groupName: string): void {
    this._selectGroup(groupId, groupName);
    this._modals[groupAction].open();
  }

  public closeActionModal(groupAction: GroupAction): void {
    this._modals[groupAction].close();
  }

  public async removeGroup(): Promise<void> {
    this.closeActionModal(GroupAction.REMOVE);
    this._setPageLoading(true);
    await removeGroup(this._selectedGroup.id);
    await this._updatePageData();
  }

  public async archiveGroup(): Promise<void> {
    this.closeActionModal(GroupAction.ARCHIVE);
    this._setPageLoading(true);
    await archiveGroup(this._selectedGroup.id);
    await this._updatePageData();
  }

  constructor({ setPageLoading, updatePageData }: ConstructorParams) {
    this._setPageLoading = setPageLoading;
    this._updatePageData = updatePageData;
    makeObservable<
      GroupInteractionDelegate,
      '_modals' | '_selectedGroup' | '_selectGroup' | '_setPageLoading' | '_updatePageData'
    >(this, {
      _modals: observable,
      _selectedGroup: observable,
      _selectGroup: action.bound,
      _setPageLoading: action,
      _updatePageData: action,
      archiveGroup: action.bound,
      removeGroup: action.bound,
      selectedGroup: computed,
      removeModal: computed,
      archiveModal: computed,
      closeActionModal: action.bound,
    });
  }
}
